import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { isAtOrUnder } from '@/lib/storageTree'
import { slotOptionsFor } from '@/lib/putAwayFilter'
import {
  useSetLotLocation,
  useStorageConditions,
  useStorageRoots,
  useFreeSlots,
} from '@/hooks/useStorage'
import { partitionByCondition } from '@/lib/storageEligibility'
import { KeptAtBadge } from './KeptAtBadge'
import type { StorageCondition } from '@/types/storage'
import { useLocationText } from '@/hooks/useStoragePlaces'

/**
 * Put one lot away, move it, or take it out — from wherever the lot is listed.
 *
 * The same operation the Storage screens perform, offered where stock is
 * managed. Before this, setting a location from the materials table meant
 * typing a legacy zone and bin: free text that no slot map could render and no
 * storage condition could be checked against, which is exactly what filled the
 * unresolved-location queue.
 *
 * "Not put away" is offered as a destination because taking stock off the shelf
 * is a real move, not an absence of one.
 */
export function SetLotLocationDialog({
  lotId,
  lotNumber,
  currentNodeId,
  currentPath = null,
  requiresCondition = null,
  open,
  onClose,
}: {
  lotId: string | null
  lotNumber?: string
  currentNodeId?: string | null
  /**
   * Where the lot is now, materialised by the server.
   *
   * Needed to OPEN on the current location rather than blank: without it the
   * dialog could keep the existing slot selectable but could not say which one
   * it was, so re-filing stock meant finding it again from the top.
   */
  currentPath?: string | null
  /**
   * What the material needs. The unit list is narrowed to what can hold it —
   * the server refuses a mismatch either way, and being told that after
   * choosing is not a useful place to learn it.
   */
  requiresCondition?: StorageCondition | null
  open: boolean
  onClose: () => void
}) {
  const locate = useLocationText()
  const roots = useStorageRoots()
  const conditions = useStorageConditions()
  const setLotLocation = useSetLotLocation()
  const [unitId, setUnitId] = useState('')
  const [slotId, setSlotId] = useState('')

  useEffect(() => {
    if (open) {
      setUnitId('')
      setSlotId('')
      setLotLocation.reset()
    }
    // `setLotLocation` is a stable mutation object; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])


  // Declared slots included (z8nrz7fqfy), and the slot it is in now so "no change" stays expressible.
  const free = useFreeSlots(unitId || null, {
    condition: requiresCondition ?? undefined,
    include: currentNodeId ?? undefined,
  })
  // Same narrowing as the receive dialog, through the same rule — two pickers
  // answering one question must not disagree about the answer.
  // Memoised because `?? []` mints a fresh array on every render while the
  // reference is loading, which would make the partition below recompute for
  // nothing on each one.
  const conditionMeta = useMemo(() => conditions.data?.conditions ?? [], [conditions.data])
  //
  // Units are matched on their own Kept at (the roots list carries nothing
  // deeper); the slots of the chosen unit are then matched on their whole
  // chain, so a rack kept at another temperature drops its slots too.
  const { eligible: units } = useMemo(
    () => partitionByCondition(roots.data ?? [], requiresCondition, conditionMeta),
    [roots.data, requiresCondition, conditionMeta],
  )

  // A slot the lot could actually go into: free, unblocked, not the holding
  // area, and kept at what the material needs — plus wherever it already is,
  // so "no change" is representable even if that place no longer matches.
  const { options: freeSlots, freeCount } = useMemo(
    () => slotOptionsFor(free.data?.nodes ?? [], requiresCondition, conditionMeta, currentNodeId),
    [free.data, currentNodeId, requiresCondition, conditionMeta],
  )

  /**
   * The unit the lot is in right now, whether or not it is an eligible
   * DESTINATION.
   *
   * Resolved against every root rather than the narrowed `units`: a lot can be
   * sitting in a unit that could not legally take it — received before the
   * material's condition was set, or moved by hand — and hiding that would
   * leave the picker blank with no way to see, or correct, where the stock
   * actually is. Matched by PATH via the segment-aware `isAtOrUnder`, so a lot
   * in "LN2-A/R10" is not attributed to the unit "LN2-A/R1".
   */
  const owningUnit = useMemo(
    () =>
      currentPath
        ? ((roots.data ?? []).find((u) => isAtOrUnder(currentPath, u.path)) ?? null)
        : null,
    [roots.data, currentPath],
  )

  /**
   * Destinations, plus wherever the lot already is.
   *
   * The current unit is appended when the condition filter would otherwise drop
   * it: a `Select` whose value is not among its items renders blank, so without
   * this the preselect below would silently show nothing.
   */
  const unitOptions = useMemo(
    () =>
      owningUnit && !units.some((u) => u.id === owningUnit.id)
        ? [...units, owningUnit]
        : units,
    [units, owningUnit],
  )

  /**
   * Open on that unit. Runs only while `unitId` is still empty, so it seeds the
   * picker once and never fights a choice the user has since made.
   *
   * Declared after the memos above because a dependency array is evaluated
   * during render — referencing either before its `const` would throw.
   */
  useEffect(() => {
    if (!open || unitId || !owningUnit) return
    setUnitId(owningUnit.id)
  }, [open, unitId, owningUnit])

  /** Then the slot it sits in, once that unit's subtree has loaded. */
  useEffect(() => {
    if (!open || slotId || !currentNodeId) return
    if (freeSlots.some((n) => n.id === currentNodeId)) setSlotId(currentNodeId)
  }, [open, slotId, currentNodeId, freeSlots])

  // `unitOptions`, not `units`: the current unit may not be an eligible
  // destination, and looking it up in the narrowed list would blank its
  // condition badge and slot count for exactly the lot that needs moving.
  const chosenUnit = unitOptions.find((u) => u.id === unitId) ?? null
  // A unit is a root, so its own Kept at is the whole answer.
  const unitCondition = chosenUnit?.providesCondition ?? null

  const commit = (storageNodeId: string | null) => {
    if (!lotId) return
    setLotLocation.mutate({ lotId, storageNodeId }, { onSuccess: onClose })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader className='space-y-1'>
          <DialogTitle className='text-lg'>Set location</DialogTitle>
          <p className='flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
            {lotNumber}
            {requiresCondition && <KeptAtBadge condition={requiresCondition} />}
          </p>
          {/* Where it is now, stated before anything is changed: this dialog
              both files new stock and moves stock already on a shelf, and the
              second reads as the first without it. */}
          <div className='text-sm'>
            <span className='text-muted-foreground'>Currently: </span>
            {currentPath ? (
              <span className='font-medium text-gray-900'>{locate(currentPath)}</span>
            ) : (
              <span className='italic text-muted-foreground'>not put away</span>
            )}
          </div>
        </DialogHeader>

        <div className='space-y-3'>
          {/* The lot's own badge in the header already says what it needs;
              only the no-requirement case needs saying here. */}
          {!requiresCondition && (
            <p className='text-xs text-muted-foreground'>
              This material has no required temperature — any place can take it.
            </p>
          )}

          <div className='space-y-1'>
            <Label>Storage unit</Label>
            <Select
              value={unitId}
              onValueChange={(v) => {
                setUnitId(v)
                setSlotId('')
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder='Choose a unit…' />
              </SelectTrigger>
              <SelectContent>
                {unitOptions.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name ?? u.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosenUnit && (
              <p className='flex flex-wrap items-center gap-2 pt-1 text-xs text-muted-foreground'>
                <KeptAtBadge condition={unitCondition} />
                {!free.isLoading && <span>{(free.data?.freeTotal ?? freeCount).toLocaleString()} free slots</span>}
              </p>
            )}
            {requiresCondition && units.length > 0 && (
              <p className='pt-1 text-xs text-muted-foreground'>
                Only places kept at this lot's temperature are listed.
              </p>
            )}
            {requiresCondition && !roots.isLoading && units.length === 0 && (
              <p className='flex flex-wrap items-center gap-1.5 pt-1 text-xs text-amber-700'>
                No place is kept at <KeptAtBadge condition={requiresCondition} />. Set a unit's Kept at in
                Storage first.
                <Link to='/inventory?tab=storage' className='font-medium text-primary hover:underline' onClick={onClose}>
                  Go to Storage
                </Link>
              </p>
            )}
          </div>

          {unitId && (
            <div className='space-y-1'>
              <Label>Slot</Label>
              <Select
                value={slotId}
                onValueChange={setSlotId}
                disabled={free.isLoading || freeSlots.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      free.isLoading
                        ? 'Loading…'
                        : freeSlots.length === 0
                          ? requiresCondition
                            ? 'No free slot here is kept at that temperature'
                            : 'No free slots in this unit'
                          : freeCount === 0
                            ? 'No other free slot — only where it is now'
                            : `Choose from ${(free.data?.freeTotal ?? freeCount).toLocaleString()} free…`
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {freeSlots.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {locate(s.path)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {setLotLocation.error && (
            <p className='text-sm text-destructive'>{String(setLotLocation.error.message)}</p>
          )}
        </div>

        <div className='flex items-center justify-between gap-2 pt-1'>
          {currentNodeId ? (
            <Button
              variant='ghost'
              size='sm'
              className='text-muted-foreground hover:text-destructive'
              disabled={setLotLocation.isPending}
              onClick={() => commit(null)}
            >
              Take out of storage
            </Button>
          ) : (
            <span />
          )}
          <div className='flex gap-2'>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!slotId || setLotLocation.isPending}
              onClick={() => commit(slotId)}
            >
              {setLotLocation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
